import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { useBrowseFilters } from '../../application/cars/browseState';
import { useCarCatalogue } from '../../application/cars/useCarCatalogue';
import useCarDeck from '../../application/cars/useCarDeck';
import useCarCollection from '../../application/game/useCarCollection';
import useCardGestures from '../../application/motion/useCardGestures';
import { carName, findCar } from '../../domain/cars/carCatalogue';
import { deckSeats, deckStep } from '../../domain/cars/carDeck';
import CarCompareLink from '../cars/CarCompareLink';
import CarDeck from '../cars/CarDeck';
import CarDossier from '../cars/CarDossier';
import CarLockedPanel from '../cars/CarLockedPanel';
import CarScreen from '../cars/CarScreen';
import CarShareSheet from '../cars/CarShareSheet';
import CarViewer from '../cars/CarViewer';
import { BackIcon, WheelIcon } from '../icons/ControlIcons';

/**
 * One car, as a card in a deck (FR-05, FR-06).
 *
 * The screen is a stage of two parts. The CARD is the first screenful — artwork
 * bleeding to the top edge over the car's own livery, the name, and the four
 * figures — and it is what a shared link opens on. Everything below it reads in
 * one order and answers one question at a time: the two actions, the full
 * specification, the prose and the other pictures.
 *
 * SWIPING SIDEWAYS DEALS THE NEXT CAR, and it deals from the shelf the reader
 * arrived on: the garage's filter query rides in the URL, `useCarDeck` reads it
 * back, and a deck built from a filtered garage is that garage. Somebody who
 * narrowed to Japanese icons and opened the RX-7 swipes through Japanese icons.
 *
 * The gesture is not the only way through and never can be (NFR-04). The arrow
 * keys step the deck, `CarDeckControls` draws a button for each direction, and
 * both do exactly what a swipe does — there is one `step`, and three ways to ask
 * for it.
 *
 * This file is the COMPOSITION. Every band is its own component under `ui/cars/`,
 * which is what keeps this readable as the order the page is in.
 *
 * SINCE SPEC-010 THE DECK IS THE READER'S OWN CARS, and a car reached by ADDRESS
 * before it has been won gets `CarLockedPanel` instead of this screen. That case
 * is the important one: the ceremony's share sends this exact URL to somebody
 * who has not won the car, so it has to be an invitation rather than a wall.
 */

/**
 * The app scrolls in `.app-shell__content`, not the window.
 *
 * A swipe lands on a car the reader has not scrolled, including one they swiped
 * for from two thirds of the way down the last page — so the new card has to
 * start at its own top. Written as a query rather than a ref because the
 * container belongs to the shell and outlives every screen in it.
 */
function scrollToTop(documentObject = typeof document === 'undefined' ? null : document) {
    const container = documentObject?.querySelector('.app-shell__content');

    if (container) {
        container.scrollTop = 0;
    }
}

/**
 * Is this key press the reader stepping the deck, or is it meant for something
 * else on the screen?
 *
 * A modifier means the browser's own shortcut, and a press inside an open dialog
 * belongs to that dialog — the share chooser's rows are reached with the arrow
 * keys on several screen readers, and stepping the deck out from underneath one
 * would be the page moving while somebody reads a menu.
 */
function stepsTheDeck(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return false;
    }

    return !event.target?.closest?.('dialog');
}

export default function CarDetailScreen() {
    const { carId } = useParams();
    const { localeCode, languageCode } = useLocale();
    const t = useTranslate();
    const { cars, status } = useCarCatalogue();
    const { unlocked } = useCarCollection({ cars });
    const { filters } = useBrowseFilters();
    // From the reader's own cars, with the garage's filter query narrowing it on
    // top: somebody who filtered to Japanese icons swipes the ones they have.
    const { deal, deck, dealing, step } = useCarDeck({
        cars: unlocked, carId, filters, localeCode, languageCode,
    });
    const car = findCar(cars, carId);
    const pageRef = useRef(null);
    // Which take the full-screen viewer opened on, or null while it is shut
    // (SPEC-012). Held HERE rather than in the two bands that summon it, so
    // there is one dialog on the screen and it sits under `.car-detail` — which
    // is where `--livery` is set, and the top layer moves where a dialog is
    // painted rather than where it lives.
    const [viewerTake, setViewerTake] = useState(null);
    const closeViewer = useCallback(() => setViewerTake(null), []);
    // Which side the next car drives in from, remembered ACROSS the deal.
    // The exit is read while `dealing` is set; the entrance is read one render
    // LATER, when the seat behind has been promoted and `dealing` has already
    // cleared — so the direction has to outlive the state that carried it.
    const enterFrom = useRef(0);

    if (dealing) {
        enterFrom.current = dealing === 'forward' ? 1 : -1;
    }

    // A new car arrives at its own top, and the caret goes with it. The two
    // landing in different places is the same bug for anyone reading by keyboard
    // or by ear: the page would be showing the 959 while focus was still inside
    // the F40's spec table.
    useEffect(() => {
        scrollToTop();
        pageRef.current?.focus?.({ preventScroll: true });
        // A card dealt out from under an open viewer would leave the reader
        // looking at the pictures of a car the page is no longer on. Closing is
        // the honest answer: they asked for the next car.
        setViewerTake(null);
    }, [carId]);

    // On WINDOW rather than on the article, so the arrows work wherever the
    // reader's focus happens to be on this screen — which after a swipe is the
    // article, and after a tab press is a link three bands down.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (!stepsTheDeck(event)) {
                return;
            }

            if (event.key === 'ArrowRight') {
                step('forward');
            } else if (event.key === 'ArrowLeft') {
                step('back');
            }
        };

        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [step]);

    // Vertical is the reader scrolling the page, always: unlike the sibling
    // product's card there is nothing to lift open here — the whole page is
    // below the card already — so the hook's default policy is the right one and
    // a vertical gesture abandons itself the moment its axis is known.
    const { handlers, gesture } = useCardGestures({ onStep: step });

    if (status === 'loading') {
        return <p className="car-detail__status">{t('garage.loading')}</p>;
    }

    // An unknown id is a mistyped or retired URL, not a crash. The catalogue is
    // content and its shape changes without a deploy, so this has to be a
    // designed state rather than an error boundary.
    if (!car) {
        return (
            <section className="car-detail car-detail--missing">
                <div className="empty-state">
                    <WheelIcon className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('cars.notFound')}</p>
                    <Link className="button button--quiet" to={`/${localeCode}/garage`}>
                        {t('cars.backToGarage')}
                    </Link>
                </div>
            </section>
        );
    }

    // Checked AFTER the unknown id, because the two are different answers: one
    // says the address is wrong, the other says it is right and the car is not
    // this reader's yet.
    if (!unlocked.some((member) => member.id === car.id)) {
        return <CarLockedPanel car={car} />;
    }

    const swiping = gesture?.axis === 'horizontal' ? gesture : null;
    // Damped to a third where there is nowhere to go, which on a ring is only a
    // shelf of one: a card that answered a drag by moving and then not changing
    // would be lying about what it did.
    const damping = swiping && !deckStep(deck, swiping.direction) ? 1 / 3 : 1;
    // While a card is on its way out, the shift is the direction it WENT rather
    // than the direction a finger is pointing — the finger has gone.
    const leaving = dealing ?? swiping?.direction;
    const seats = deckSeats(deck);

    return (
        <article
            className={[
                'car-detail',
                swiping ? 'car-detail--swiping' : '',
                dealing ? 'car-detail--dealing' : '',
            ].filter(Boolean).join(' ')}
            // ONE custom property per frame, on ONE element. The card's
            // translate, its tilt and the stack's rise are all CSS expressions
            // over these — which is what keeps a drag at one style write per
            // frame instead of one per moving part.
            style={{
                '--livery': `var(--livery-${car.livery})`,
                '--deck-progress': swiping ? swiping.progress * damping : 0,
                '--deck-shift': leaving === 'forward' ? -1 : 1,
                '--deck-enter': enterFrom.current,
                '--deck-deal': `${deal}ms`,
            }}
            // Where focus lands when a swipe brings a new car in. The container
            // rather than its heading, because the way back and the picture come
            // BEFORE the name in the document, and a reader put on the heading
            // would have both behind them.
            ref={pageRef}
            tabIndex={-1}
        >
            {/* Every other route change follows an activated link, which a
                screen reader announces. A swipe activates nothing, so without
                this the page silently becomes a different car. */}
            <p className="car-detail__now-showing visually-hidden" role="status">
                {t('cars.deck.nowShowing', { car: carName(car) })}
            </p>

            {/* Sticky and zero-height, so it floats over whatever the reader has
                scrolled to rather than leaving with the artwork. */}
            <div className="car-detail__back">
                <Link
                    className="round-button"
                    to={`/${localeCode}/garage`}
                    aria-label={t('cars.backToGarage')}
                >
                    <BackIcon size={22} />
                </Link>
            </div>

            <CarDeck
                car={car}
                deck={deck}
                seats={seats}
                dismissHint={Boolean(swiping || dealing)}
                handlers={handlers}
                onStep={step}
                onOpenPicture={setViewerTake}
            />

            <div className="car-detail__body">
                {/* THE DATA BLOCK — the specification and the graph in one
                    bordered box, which is the bottom half of the screen this
                    route is composed to (SPEC-004 PR-4.8).

                    NOT KEYED. It was, so that a deal rebuilt it and its arrival
                    animation ran again — and rebuilding is exactly what a reader
                    reported as the page repainting itself. The block is mounted
                    once and its VALUES change: the specification's figures
                    cross-fade in place and the graph redraws its own trace,
                    while the axes, the headings and the frame never move. */}
                <CarScreen car={car} cars={cars} />

                {/* THE TWO ACTIONS: send this car to somebody, or put it on a
                    strip against another.

                    At the FOOT of the screen since the page was composed to the
                    reference, and that reverses PR-8.13, which put them first in
                    the body on the argument that the reader most likely to act
                    on a car is the one who has just arrived at it. That argument
                    still holds and it is not what changed — what changed is that
                    the artwork and the data block are now ONE object, the way
                    the reference draws them, and two buttons wedged between the
                    plate and the specification cut it in half. Here they close
                    the screen instead: the car, its figures, and then what a
                    reader can do about it.

                    In the flow rather than pinned to the foot of the viewport:
                    the shell is sized to the DYNAMIC viewport so it does not
                    jump as Safari's toolbar animates, which leaves the last
                    stripe of the scrollport behind that toolbar, and a bar stuck
                    to `bottom: 0` lands in it showing only its top edge. */}
                <div className="car-screen__foot">
                    <div className="car-actions">
                        <CarShareSheet car={car} />
                        <CarCompareLink car={car} />
                    </div>
                </div>

                {/* And everything the screen is not — what it has been worth,
                    the prose, the people and the pictures. Simply below, reached
                    by scrolling: it was behind a fold and the fold is gone
                    (SPEC-004 PR-4.9). Not keyed either, for the reason the block
                    above is not — there is no state left in it to reset. */}
                <CarDossier car={car} onOpenTake={setViewerTake} />
            </div>

            {/* Mounted only while it is open, because `showModal()` is called in
                a mount effect: a dialog that stayed in the tree would need a
                second effect to decide when to open it, and two writers on one
                element's modality is how a dialog ends up open with nothing
                behind it. Last in the article so it is the last thing the
                document offers — its own position in the tree is what carries
                `--livery` into it, and the top layer decides where it is drawn
                (SPEC-012 D-11). */}
            {viewerTake ? (
                <CarViewer car={car} startTake={viewerTake} onClose={closeViewer} />
            ) : null}
        </article>
    );
}
